import { useState, useRef, useEffect } from 'react';
import * as turf from '@turf/turf';
import {
  Plus,
  AlertCircle, RotateCcw, CheckCircle2, Trash2
} from 'lucide-react';

import { postDataWithBody, getData } from '../../../Services/Services';
import { creategeofence, getroutname, getroutlatlon, getgeofencecategories } from '../../../Services/EndPoints';
import { toast } from 'react-toastify';
import AssignGeofencing from './AssignGeofencing';
import './Geofence.css';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg';

/* ─────────────────────────────────────────────
   Load Google Maps script once
   Requires: places, geometry
───────────────────────────────────────────── */
let googleMapsPromise = null;
const loadGoogleMaps = () => {
  if (googleMapsPromise) return googleMapsPromise;
  googleMapsPromise = new Promise((resolve, reject) => {
    // Already loaded with geometry — reuse
    if (window.google && window.google.maps && window.google.maps.geometry) {
      resolve(window.google.maps);
      return;
    }

    // Loaded without geometry (e.g. by another library) — reload with correct libs
    if (window.google?.maps) {
      document.querySelectorAll('script[src*="maps.googleapis.com"]').forEach(s => s.remove());
      delete window.google;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&v=weekly&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;

    window.__googleMapsCallback = () => {
      if (window.google?.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps failed to initialize'));
      }
      delete window.__googleMapsCallback;
    };

    script.onerror = () => {
      googleMapsPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };

    document.head.appendChild(script);
  });
  return googleMapsPromise;
};

/* ─────────────────────────────────────────────
   Shape types config
───────────────────────────────────────────── */
const SHAPE_TYPES = [
  {
    id: 'polygon',
    label: 'Polygon',
    icon: (active) => (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <polygon
          points="14,3 26,10 22,24 6,24 2,10"
          stroke={active ? '#0f4a45' : '#94a3b8'}
          strokeWidth="2"
          fill={active ? 'rgba(26,122,115,0.12)' : 'transparent'}
          strokeLinejoin="round"
        />
      </svg>
    )
  },
  {
    id: 'circle',
    label: 'Circle',
    icon: (active) => (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle
          cx="14" cy="14" r="11"
          stroke={active ? '#0f4a45' : '#94a3b8'}
          strokeWidth="2"
          fill={active ? 'rgba(26,122,115,0.12)' : 'transparent'}
        />
      </svg>
    )
  },
  {
    id: 'line',
    label: 'Line',
    icon: (active) => (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="4" cy="22" r="3" fill={active ? '#0f4a45' : '#94a3b8'} />
        <circle cx="14" cy="8" r="3" fill={active ? '#0f4a45' : '#94a3b8'} />
        <circle cx="24" cy="18" r="3" fill={active ? '#0f4a45' : '#94a3b8'} />
        <polyline
          points="4,22 14,8 24,18"
          stroke={active ? '#0f4a45' : '#94a3b8'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    )
  }
];

/* ─────────────────────────────────────────────
   Map Layer Tile URLs
───────────────────────────────────────────── */
const MAP_TYPE_IDS = {
  road: 'roadmap',
  satellite: 'satellite',
  terrain: 'terrain',
  hybrid: 'hybrid'
};

/* ─────────────────────────────────────────────
   Create Geofence View (Google Maps version)
───────────────────────────────────────────── */
const CreateGeofenceView = () => {
  const mapDivRef = useRef(null);
  const mapPanelRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnShapeRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchTimerRef = useRef(null);
  const geocoderRef = useRef(null);
  const infoWindowRef = useRef(null);
  const transitLayerRef = useRef(null);
  const shapeEventListenersRef = useRef([]);

  /* Custom drawing refs — replaces DrawingManager */
  const currentPathRef = useRef([]);          // vertices being drawn
  const tempPolylineRef = useRef(null);       // preview polyline
  const tempMarkersRef = useRef([]);          // vertex markers
  const circleCenterRef = useRef(null);       // circle center { lat, lng }
  const circleCenterMarkerRef = useRef(null);
  const tempCircleRef = useRef(null);         // preview circle
  const mapListenersRef = useRef([]);

  /* Mirror refs — give stable event-listener closures access to current state */
  const activeShapeRef = useRef(null);
  const drawingModeRef = useRef(false);
  const colorRef = useRef('#1a7a73');

  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [activeShape, setActiveShape] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState('road');
  const [showLayerControl, setShowLayerControl] = useState(false);
  const [errors, setErrors] = useState({ name: '', zone: '', category: '', boundary: '' });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [shapeDrawn, setShapeDrawn] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTransit, setShowTransit] = useState(false);

  const [newGeofence, setNewGeofence] = useState({
    name: '', zone: '', category: '', type: 'polygon', color: '#1a7a73',
    coordinates: null, center: null, radius: null, area: 0
  });

  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const routeMarkersRef = useRef([]);
  const routePolylineRef = useRef(null);

  /* Keep mirror refs in sync with state */
  useEffect(() => { activeShapeRef.current = activeShape; }, [activeShape]);
  useEffect(() => { drawingModeRef.current = drawingMode; }, [drawingMode]);
  useEffect(() => { colorRef.current = newGeofence.color; }, [newGeofence.color]);

  /* ── Load Google Maps ── */
  useEffect(() => {
    loadGoogleMaps()
      .then(() => setMapsLoaded(true))
      .catch(err => console.error('Google Maps load error:', err));
  }, []);

  /* ── Fetch routes ── */
  useEffect(() => {
    getData(getroutname)
      .then(res => { if (Array.isArray(res)) setRoutes(res); })
      .catch(() => {});
  }, []);

  /* ── Fetch geofence categories ── */
  useEffect(() => {
    getData(getgeofencecategories)
      .then(res => { if (Array.isArray(res?.data)) setCategories(res.data); })
      .catch(() => {});
  }, []);

  /* ── Fetch waypoints when route changes ── */
  useEffect(() => {
    if (!selectedRouteId) { setWaypoints([]); return; }
    getData(getroutlatlon.replace('ID', selectedRouteId))
      .then(res => {
        const list = Array.isArray(res) ? res
          : (res && Array.isArray(res.waypoints)) ? res.waypoints : [];
        setWaypoints(list);
      })
      .catch(() => setWaypoints([]));
  }, [selectedRouteId]);

  /* ── Render / clear route on map when waypoints or map changes ── */
  useEffect(() => {
    routeMarkersRef.current.forEach(m => m.setMap(null));
    routeMarkersRef.current = [];
    if (routePolylineRef.current) { routePolylineRef.current.setMap(null); routePolylineRef.current = null; }

    if (!mapsLoaded || !mapInstanceRef.current || !window.google || waypoints.length === 0) return;
    const google = window.google;
    const map = mapInstanceRef.current;

    const haltSvg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
      <circle cx="17" cy="17" r="16" fill="#ffc107" stroke="#ffffff" stroke-width="2"/>
      <rect x="10" y="10" width="5" height="14" rx="1.5" fill="#ffffff"/>
      <rect x="19" y="10" width="5" height="14" rx="1.5" fill="#ffffff"/>
    </svg>`);

    const makeIcon = (type) => {
      if (type === 'start') return {
        url: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
        scaledSize: new google.maps.Size(25, 41),
        anchor: new google.maps.Point(12, 41),
        labelOrigin: new google.maps.Point(12, 14),
      };
      if (type === 'end') return {
        url: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
        scaledSize: new google.maps.Size(25, 41),
        anchor: new google.maps.Point(12, 41),
        labelOrigin: new google.maps.Point(12, 14),
      };
      if (type === 'halt') return {
        url: `data:image/svg+xml;charset=UTF-8,${haltSvg}`,
        scaledSize: new google.maps.Size(34, 34),
        anchor: new google.maps.Point(17, 17),
      };
      return null;
    };

    const typeColor = (type) =>
      type === 'start' ? '#28a745' : type === 'end' ? '#dc3545' : '#ffc107';

    const typeLabel = (type) =>
      type === 'start' ? 'Start Point' : type === 'end' ? 'End Point' : 'Halt Point';

    const geocodeCache = {};
    const geocoder = new google.maps.Geocoder();

    const getAddress = (lat, lng) => new Promise(resolve => {
      const key = `${lat},${lng}`;
      if (geocodeCache[key]) { resolve(geocodeCache[key]); return; }
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        const addr = status === 'OK' && results[0]
          ? results[0].formatted_address
          : 'Address not found';
        geocodeCache[key] = addr;
        resolve(addr);
      });
    });

    const buildInfoContent = (label, color, address, waitTime) => `
      <div style="font-family:'DM Sans',sans-serif;min-width:240px;padding:14px 16px 14px 16px;padding-right:36px">
        <h6 style="margin:0 0 8px 0;font-size:13px;font-weight:700;color:${color};border-bottom:2px solid ${color};padding-bottom:6px">${label}</h6>
        <div style="font-size:12px;line-height:1.6;color:#333">
          <strong>Address:</strong><br/>
          <span style="color:#555">${address}</span>
          ${waitTime ? `<div style="margin-top:10px;padding:6px 12px;background:#fef9c3;border:1px solid #fde68a;border-radius:6px;font-size:12px;font-weight:600;color:#92400e"><strong>Wait Time:</strong> <span style="color:#b45309">${waitTime} mins</span></div>` : ''}
        </div>
      </div>`;

    const path = [];
    waypoints.forEach(pt => {
      const lat = parseFloat(pt.latitude);
      const lng = parseFloat(pt.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      path.push({ lat, lng });

      const type = pt.pointType?.toLowerCase();
      const icon = makeIcon(type);
      if (!icon) return;

      const marker = new google.maps.Marker({ position: { lat, lng }, map, icon, zIndex: 10 });

      marker.addListener('click', async () => {
        if (!infoWindowRef.current) return;
        const label = typeLabel(type);
        const color = typeColor(type);
        infoWindowRef.current.setContent(buildInfoContent(label, color, 'Loading address…', null));
        infoWindowRef.current.open(map, marker);
        const address = await getAddress(lat, lng);
        infoWindowRef.current.setContent(
          buildInfoContent(label, color, address, pt.waitTime || null)
        );
      });

      routeMarkersRef.current.push(marker);
    });

    if (path.length > 1) {
      routePolylineRef.current = new google.maps.Polyline({
        path,
        strokeColor: '#0f4a45',
        strokeOpacity: 0.85,
        strokeWeight: 4,
        map,
      });
    }

    if (path.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      path.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, 40);
    }
  }, [waypoints, mapsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Helper: clear shape event listeners ── */
  const clearShapeEventListeners = () => {
    if (!window.google) return;
    shapeEventListenersRef.current.forEach(listener => {
      window.google.maps.event.removeListener(listener);
    });
    shapeEventListenersRef.current = [];
  };

  /* ── Initialize Map ── */
  useEffect(() => {
    if (!mapsLoaded || !mapDivRef.current) return;
    if (!window.google || !window.google.maps) return;
    if (mapInstanceRef.current) return;

    const google = window.google;

    const map = new google.maps.Map(mapDivRef.current, {
      center: { lat: 17.385, lng: 78.486 },
      zoom: 13,
      mapTypeId: MAP_TYPE_IDS['road'],
      mapTypeControl: false,
      streetViewControl: true,
      fullscreenControl: false,
      zoomControl: true,
      scaleControl: false,
      rotateControl: false,
      clickableIcons: false,
      gestureHandling: 'greedy',
      minZoom: 5
    });
    mapInstanceRef.current = map;
    transitLayerRef.current = new google.maps.TransitLayer();

    /* ── Custom drawing helpers ── */

    const clearTempShapes = () => {
      if (tempPolylineRef.current) { tempPolylineRef.current.setMap(null); tempPolylineRef.current = null; }
      if (tempCircleRef.current) { tempCircleRef.current.setMap(null); tempCircleRef.current = null; }
      if (circleCenterMarkerRef.current) { circleCenterMarkerRef.current.setMap(null); circleCenterMarkerRef.current = null; }
      tempMarkersRef.current.forEach(m => m.setMap(null));
      tempMarkersRef.current = [];
      currentPathRef.current = [];
      circleCenterRef.current = null;
    };

    const updatePreviewLine = (extraPoint) => {
      const base = currentPathRef.current;
      if (base.length === 0) return;
      const pts = extraPoint ? [...base, extraPoint] : [...base];
      if (activeShapeRef.current === 'polygon' && pts.length >= 2) pts.push(base[0]);
      if (pts.length < 2) return; // need at least 2 points to draw a visible line
      if (tempPolylineRef.current) {
        tempPolylineRef.current.setPath(pts);
      } else {
        tempPolylineRef.current = new google.maps.Polyline({
          path: pts,
          strokeColor: colorRef.current,
          strokeOpacity: 0.7,
          strokeWeight: 2,
          clickable: false, // never intercept map clicks
          map,
        });
      }
    };

    const finishPolygonOrLine = (shapeType, path) => {
      drawingModeRef.current = false;
      activeShapeRef.current = null;
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });

      // Remove trailing duplicate vertices (Google Maps may fire a click before dblclick)
      const cleanPath = path.filter((pt, i) =>
        i === 0 || !(pt.lat === path[i - 1].lat && pt.lng === path[i - 1].lng)
      );

      if (shapeType === 'polygon' && cleanPath.length < 3) { clearTempShapes(); return; }
      if (shapeType === 'line' && cleanPath.length < 2) { clearTempShapes(); return; }

      // Replace path arg with deduplicated version for shape creation below
      path = cleanPath;

      clearTempShapes();
      if (drawnShapeRef.current) { drawnShapeRef.current.setMap(null); drawnShapeRef.current = null; }
      clearShapeEventListeners();

      const color = colorRef.current;

      if (shapeType === 'polygon') {
        const shape = new google.maps.Polygon({
          paths: path,
          fillColor: color,
          fillOpacity: 0.25,
          strokeColor: color,
          strokeWeight: 2,
          editable: true,
          draggable: true,
          clickable: true,
          map,
        });

        const updatePolygon = () => {
          try {
            const coords = shape.getPath().getArray().map(p => [p.lat(), p.lng()]);
            if (coords.length >= 3) {
              const closedCoords = [...coords, coords[0]];
              const turfPoly = turf.polygon([closedCoords.map(([lat, lng]) => [lng, lat])]);
              const area = Math.round(turf.area(turfPoly));
              setNewGeofence(prev => ({ ...prev, type: 'polygon', coordinates: coords, center: null, radius: null, area }));
            }
          } catch (err) { console.error('Polygon update error:', err); }
        };

        const pp = shape.getPath();
        shapeEventListenersRef.current = [
          google.maps.event.addListener(pp, 'set_at', updatePolygon),
          google.maps.event.addListener(pp, 'insert_at', updatePolygon),
          google.maps.event.addListener(pp, 'remove_at', updatePolygon),
        ];
        updatePolygon();
        drawnShapeRef.current = shape;

      } else {
        const shape = new google.maps.Polyline({
          path,
          strokeColor: color,
          strokeWeight: 3,
          editable: true,
          draggable: true,
          clickable: true,
          map,
        });

        const updateLine = () => {
          try {
            const coords = shape.getPath().getArray().map(p => [p.lat(), p.lng()]);
            if (coords.length >= 2) {
              setNewGeofence(prev => ({ ...prev, type: 'line', coordinates: coords, center: null, radius: null, area: 0 }));
            }
          } catch (err) { console.error('Line update error:', err); }
        };

        const lp = shape.getPath();
        shapeEventListenersRef.current = [
          google.maps.event.addListener(lp, 'set_at', updateLine),
          google.maps.event.addListener(lp, 'insert_at', updateLine),
          google.maps.event.addListener(lp, 'remove_at', updateLine),
        ];
        updateLine();
        drawnShapeRef.current = shape;
      }

      setDrawingMode(false);
      setShapeDrawn(true);
      setActiveShape(null);
      setErrors(prev => ({ ...prev, boundary: '' }));
    };

    const finishCircle = (center, radius) => {
      drawingModeRef.current = false;
      activeShapeRef.current = null;
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
      clearTempShapes();
      if (drawnShapeRef.current) { drawnShapeRef.current.setMap(null); drawnShapeRef.current = null; }
      clearShapeEventListeners();

      const color = colorRef.current;
      const shape = new google.maps.Circle({
        center,
        radius: Math.max(radius, 10),
        fillColor: color,
        fillOpacity: 0.25,
        strokeColor: color,
        strokeWeight: 2,
        editable: true,
        draggable: true,
        clickable: true,
        map,
      });

      const updateCircle = () => {
        try {
          const c = shape.getCenter();
          const r = shape.getRadius();
          setNewGeofence(prev => ({
            ...prev,
            type: 'circle',
            center: [c.lat(), c.lng()],
            radius: r,
            coordinates: null,
            area: Math.round(Math.PI * r * r)
          }));
        } catch (err) { console.error('Circle update error:', err); }
      };

      shapeEventListenersRef.current = [
        google.maps.event.addListener(shape, 'radius_changed', updateCircle),
        google.maps.event.addListener(shape, 'center_changed', updateCircle),
      ];
      updateCircle();
      drawnShapeRef.current = shape;

      setDrawingMode(false);
      setShapeDrawn(true);
      setActiveShape(null);
      setErrors(prev => ({ ...prev, boundary: '' }));
    };

    /* ── Map click: add vertex immediately ── */
    const clickListener = google.maps.event.addListener(map, 'click', (e) => {
      if (!drawingModeRef.current) return;

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const shape = activeShapeRef.current;

      if (shape === 'circle') {
        if (!circleCenterRef.current) {
          circleCenterRef.current = { lat, lng };
          if (circleCenterMarkerRef.current) circleCenterMarkerRef.current.setMap(null);
          circleCenterMarkerRef.current = new google.maps.Marker({
            position: { lat, lng },
            map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: colorRef.current,
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
            zIndex: 20,
            clickable: false, // never intercept map clicks
          });
        } else {
          const dist = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(circleCenterRef.current.lat, circleCenterRef.current.lng),
            new google.maps.LatLng(lat, lng)
          );
          finishCircle(circleCenterRef.current, dist);
        }
        return;
      }

      // polygon / line — add vertex immediately
      currentPathRef.current = [...currentPathRef.current, { lat, lng }];
      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: colorRef.current,
          fillOpacity: 0.9,
          strokeColor: '#fff',
          strokeWeight: 1.5,
        },
        zIndex: 20,
        clickable: false, // never intercept map clicks
      });
      tempMarkersRef.current.push(marker);
      updatePreviewLine(null);
    });

    /* ── Map double-click: finish polygon/line ── */
    const dblClickListener = google.maps.event.addListener(map, 'dblclick', () => {
      if (!drawingModeRef.current) return;
      const shape = activeShapeRef.current;
      if (shape === 'polygon' || shape === 'line') {
        finishPolygonOrLine(shape, [...currentPathRef.current]);
      }
    });

    /* ── Map mousemove: live preview ── */
    const moveListener = google.maps.event.addListener(map, 'mousemove', (e) => {
      if (!drawingModeRef.current) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const shape = activeShapeRef.current;

      if (shape === 'circle' && circleCenterRef.current) {
        const dist = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(circleCenterRef.current.lat, circleCenterRef.current.lng),
          new google.maps.LatLng(lat, lng)
        );
        if (tempCircleRef.current) {
          tempCircleRef.current.setRadius(dist);
        } else {
          tempCircleRef.current = new google.maps.Circle({
            center: circleCenterRef.current,
            radius: dist,
            map,
            fillColor: colorRef.current,
            fillOpacity: 0.15,
            strokeColor: colorRef.current,
            strokeOpacity: 0.5,
            strokeWeight: 2,
            clickable: false, // never intercept map clicks
          });
        }
        return;
      }

      if ((shape === 'polygon' || shape === 'line') && currentPathRef.current.length > 0) {
        updatePreviewLine({ lat, lng });
      }
    });

    mapListenersRef.current = [clickListener, dblClickListener, moveListener];

    /* ── AutocompleteService for custom search bar ── */
    if (google.maps.places) {
      autocompleteRef.current = new google.maps.places.AutocompleteService();
      geocoderRef.current = new google.maps.Geocoder();
    }

    infoWindowRef.current = new google.maps.InfoWindow();

    return () => {
      clearShapeEventListeners();
      mapListenersRef.current.forEach(l => google.maps.event.removeListener(l));
      mapListenersRef.current = [];
      if (tempPolylineRef.current) { tempPolylineRef.current.setMap(null); tempPolylineRef.current = null; }
      if (tempCircleRef.current) { tempCircleRef.current.setMap(null); tempCircleRef.current = null; }
      if (circleCenterMarkerRef.current) { circleCenterMarkerRef.current.setMap(null); circleCenterMarkerRef.current = null; }
      tempMarkersRef.current.forEach(m => m.setMap(null));
      tempMarkersRef.current = [];
      if (transitLayerRef.current) { transitLayerRef.current.setMap(null); transitLayerRef.current = null; }
      mapInstanceRef.current = null;
    };
  }, [mapsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Map type change ── */
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(MAP_TYPE_IDS[selectedLayer]);
  }, [selectedLayer]);

  /* ── Color change: update drawn shape + preview shapes ── */
  useEffect(() => {
    if (drawnShapeRef.current && typeof drawnShapeRef.current.setOptions === 'function') {
      drawnShapeRef.current.setOptions({
        fillColor: newGeofence.color,
        strokeColor: newGeofence.color,
      });
    }
    if (tempPolylineRef.current) {
      tempPolylineRef.current.setOptions({ strokeColor: newGeofence.color });
    }
    if (tempCircleRef.current) {
      tempCircleRef.current.setOptions({ fillColor: newGeofence.color, strokeColor: newGeofence.color });
    }
  }, [newGeofence.color]);

  /* ── Fullscreen detection ── */
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === mapPanelRef.current);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  /* ── Trigger map resize when fullscreen changes ── */
  useEffect(() => {
    if (mapInstanceRef.current && window.google?.maps) {
      window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
    }
  }, [isFullscreen]);

  /* ── Transit layer toggle ── */
  useEffect(() => {
    if (!transitLayerRef.current) return;
    transitLayerRef.current.setMap(showTransit ? mapInstanceRef.current : null);
  }, [showTransit]);

  /* ── Activate drawing mode ── */
  const activateShape = (shapeId) => {
    if (!mapInstanceRef.current || !window.google) return;

    if (drawnShapeRef.current) {
      drawnShapeRef.current.setMap(null);
      drawnShapeRef.current = null;
    }
    clearShapeEventListeners();

    // Clear any in-progress preview shapes
    if (tempPolylineRef.current) { tempPolylineRef.current.setMap(null); tempPolylineRef.current = null; }
    if (tempCircleRef.current) { tempCircleRef.current.setMap(null); tempCircleRef.current = null; }
    if (circleCenterMarkerRef.current) { circleCenterMarkerRef.current.setMap(null); circleCenterMarkerRef.current = null; }
    tempMarkersRef.current.forEach(m => m.setMap(null));
    tempMarkersRef.current = [];
    currentPathRef.current = [];
    circleCenterRef.current = null;

    activeShapeRef.current = shapeId;
    drawingModeRef.current = true;
    setShapeDrawn(false);
    setActiveShape(shapeId);
    setNewGeofence(prev => ({
      ...prev,
      type: shapeId,
      coordinates: null,
      center: null,
      radius: null,
      area: 0
    }));
    setDrawingMode(true);
    setErrors(prev => ({ ...prev, boundary: '' }));
    mapInstanceRef.current.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
  };

  /* ── Clear shape ── */
  const clearMapShape = () => {
    if (drawnShapeRef.current) {
      drawnShapeRef.current.setMap(null);
      drawnShapeRef.current = null;
    }
    clearShapeEventListeners();

    if (tempPolylineRef.current) { tempPolylineRef.current.setMap(null); tempPolylineRef.current = null; }
    if (tempCircleRef.current) { tempCircleRef.current.setMap(null); tempCircleRef.current = null; }
    if (circleCenterMarkerRef.current) { circleCenterMarkerRef.current.setMap(null); circleCenterMarkerRef.current = null; }
    tempMarkersRef.current.forEach(m => m.setMap(null));
    tempMarkersRef.current = [];
    currentPathRef.current = [];
    circleCenterRef.current = null;

    activeShapeRef.current = null;
    drawingModeRef.current = false;
    setShapeDrawn(false);
    setDrawingMode(false);
    setActiveShape(null);
    if (mapInstanceRef.current) mapInstanceRef.current.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });

    setNewGeofence(prev => ({
      ...prev,
      coordinates: null,
      center: null,
      radius: null,
      area: 0
    }));
  };

  /* ── Reset form ── */
  const resetForm = () => {
    clearMapShape();
    setNewGeofence({
      name: '', zone: '', category: '', type: 'polygon', color: '#1a7a73',
      coordinates: null, center: null, radius: null, area: 0
    });
    setSelectedRouteId(null);
    setErrors({ name: '', zone: '', category: '', boundary: '' });
  };

  /* ── Location search ── */
  const searchLocation = (query) => {
    if (!query.trim() || !autocompleteRef.current) { setSearchResults([]); return; }
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: 'in' } },
      (predictions, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  };

  const selectSearchResult = (place) => {
    setSearchQuery(place.description);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const viewport = results[0].geometry.viewport;
        if (viewport) {
          mapInstanceRef.current.fitBounds(viewport);
        } else {
          const loc = results[0].geometry.location;
          mapInstanceRef.current.setCenter(loc);
          mapInstanceRef.current.setZoom(15);
        }
      }
    });
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(val), 500);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  /* ── Create geofence ── */
  const shapeBoundaryReady = newGeofence.coordinates || newGeofence.center;

  const createGeofenceHandler = async () => {
    const newErrors = { name: '', zone: '', category: '', boundary: '' };
    let hasError = false;
    if (!newGeofence.name.trim()) { newErrors.name = 'Please enter a geofence name'; hasError = true; }
    if (!newGeofence.category) { newErrors.category = 'Please select a geofence category'; hasError = true; }
    if (!shapeBoundaryReady) { newErrors.boundary = 'Please draw a boundary on the map'; hasError = true; }
    if (hasError) { setErrors(newErrors); return; }

    let geometry;
    if (newGeofence.type === 'polygon') {
      geometry = {
        type: 'Polygon',
        coordinates: newGeofence.coordinates.map(([lat, lng]) => [lng, lat])
      };
    } else if (newGeofence.type === 'circle') {
      geometry = {
        type: 'Circle',
        center: { lat: newGeofence.center[0], lng: newGeofence.center[1] },
        radius: newGeofence.radius
      };
    } else {
      geometry = {
        type: 'LineString',
        coordinates: newGeofence.coordinates.map(([lat, lng]) => [lng, lat])
      };
    }

    const payload = {
      name: newGeofence.name,
      zone: newGeofence.zone,
      category: newGeofence.category,
      type: newGeofence.type,
      color: newGeofence.color,
      area: newGeofence.area,
      geometry,
      ...(selectedRouteId != null && { routeId: selectedRouteId })
    };

    try {
      setSaving(true);
      const response = await postDataWithBody(creategeofence, payload, {});
      if (response.success && response.data) {
        toast.success(response.message, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        resetForm();
      }
    } catch (err) {
      console.error('API Error:', err);
      toast.error('Failed to create geofence', { position: toast.POSITION.TOP_CENTER });
    } finally {
      setSaving(false);
    }
  };

  const LAYERS = [
    { id: 'road',      label: 'Standard',  icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg> },
    { id: 'satellite', label: 'Satellite', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> },
    { id: 'terrain',   label: 'Terrain',   icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 18l-7-7-5 5-5-5"/><path d="M2 18h20"/></svg> },
    { id: 'hybrid',    label: 'Hybrid',    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg> },
  ];

  const inputFocus = (e, hasError) => {
    if (!hasError) {
      e.target.style.borderColor = '#0f4a45';
      e.target.style.boxShadow = '0 0 0 3px rgba(26,122,115,0.12)';
    }
  };
  const inputBlur = (e, hasError) => {
    if (!hasError) {
      e.target.style.borderColor = '#e2e8f0';
      e.target.style.boxShadow = 'none';
    }
  };

  /* ── Map overlay controls ── */
  const mapControls = (
    <>
      <div style={{ position: 'absolute', top: 9, right: 9, zIndex: 1000 }}>
        <MapIconBtn
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          onClick={() => {
            if (!document.fullscreenElement) mapPanelRef.current?.requestFullscreen();
            else document.exitFullscreen();
          }}
        >
          {isFullscreen
            ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#475569"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
            : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#475569"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>}
        </MapIconBtn>
      </div>
      <div style={{ position: 'absolute', top: 58, right: 9, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ position: 'relative' }}>
          <button title="Map layers" onClick={() => setShowLayerControl(p => !p)} style={{ width: 40, height: 40, border: 'none', background: 'white', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 2 7 12 12 22 7 12 2"/>
              <polyline points="2 17 12 22 22 17"/>
              <polyline points="2 12 12 17 22 12"/>
            </svg>
          </button>
          {showLayerControl && (
            <div style={{ position: 'absolute', top: 45, right: 0, background: 'white', borderRadius: 10, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 160, border: '1px solid #e2e8f0' }}>
              {LAYERS.map(l => (
                <div key={l.id} onClick={() => { setSelectedLayer(l.id); setShowLayerControl(false); }} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 7, cursor: 'pointer', background: selectedLayer === l.id ? '#f0fdfb' : 'transparent', transition: 'background 0.15s' }}>
                  <span style={{ color: selectedLayer === l.id ? '#1a7a73' : '#94a3b8', display: 'flex' }}>{l.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: selectedLayer === l.id ? 600 : 400, color: selectedLayer === l.id ? '#1a7a73' : '#334155' }}>{l.label}</span>
                  {selectedLayer === l.id && <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: '#1a7a73' }} />}
                </div>
              ))}
            </div>
          )}
        </div>
        <MapIconBtn
          title={showTransit ? "Hide transit" : "Show transit"}
          onClick={() => setShowTransit(p => !p)}
          active={showTransit}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={showTransit ? "#0f4a45" : "#555"} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="16" rx="2"/>
            <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
            <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
          </svg>
        </MapIconBtn>
        {shapeDrawn && (
          <MapIconBtn title="Clear drawn shape" onClick={clearMapShape} danger>
            <Trash2 size={16} color="#ef4444" />
          </MapIconBtn>
        )}
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, fontFamily: "'DM Sans', sans-serif", padding: '14px 16px', gap: 16, background: '#f0f4f8', boxSizing: 'border-box' }}>

      {/* LEFT PANEL */}
      <div
        className="gf-left-panel"
        style={{
          width: 360, minWidth: 360, background: '#ffffff',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0,
          border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{
          padding: '16px 20px 14px',
          background: 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)',
          flexShrink: 0, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, right: 30, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.25 }}>Create Geofence</div>
              <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>Define a custom boundary zone on the map</div>
            </div>
          </div>
        </div>

        <div className="gf-left-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px 18px 24px' }}>

          <SectionHeader label="Geofence Details" icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="rgba(255,255,255,0.3)" />
              <circle cx="12" cy="9" r="2.5" fill="white" stroke="white" />
            </svg>
          } />

          <FieldGroup label="Geofence Name" required style={{ marginBottom: 16 }}>
            <input
              placeholder="Enter geofence name…"
              value={newGeofence.name}
              onChange={e => { setNewGeofence({ ...newGeofence, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }); }}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 9,
                border: errors.name ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                fontSize: 13.5, color: '#0f172a', outline: 'none',
                background: errors.name ? '#fef2f2' : '#f8fafc',
                boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s',
                fontFamily: "'DM Sans', sans-serif",
              }}
              onFocus={e => inputFocus(e, errors.name)}
              onBlur={e => inputBlur(e, errors.name)}
            />
            {errors.name && <FieldError msg={errors.name} />}
          </FieldGroup>

          <FieldGroup label="Geofence Category" required style={{ marginBottom: 16 }}>
            <div style={{ position: 'relative' }}>
              <select
                value={newGeofence.category}
                onChange={e => { setNewGeofence({ ...newGeofence, category: e.target.value }); if (errors.category) setErrors({ ...errors, category: '' }); }}
                style={{
                  width: '100%', padding: '9px 32px 9px 12px', borderRadius: 9,
                  border: errors.category ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                  fontSize: 13.5, color: newGeofence.category ? '#0f172a' : '#94a3b8',
                  outline: 'none', background: errors.category ? '#fef2f2' : '#f8fafc',
                  boxSizing: 'border-box', cursor: 'pointer', appearance: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onFocus={e => inputFocus(e, errors.category)}
                onBlur={e => inputBlur(e, errors.category)}
              >
                <option value="">{'—'} Select a category {'—'}</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
            {errors.category && <FieldError msg={errors.category} />}
          </FieldGroup>

          <FieldGroup label="Select Route" optional style={{ marginBottom: 16 }}>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedRouteId ?? ''}
                onChange={e => setSelectedRouteId(e.target.value ? Number(e.target.value) : null)}
                style={{
                  width: '100%', padding: '9px 32px 9px 12px', borderRadius: 9,
                  border: '1.5px solid #e2e8f0', fontSize: 13.5,
                  color: selectedRouteId ? '#0f172a' : '#94a3b8',
                  outline: 'none', background: '#f8fafc', boxSizing: 'border-box',
                  cursor: 'pointer', appearance: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onFocus={e => inputFocus(e, false)}
                onBlur={e => inputBlur(e, false)}
              >
                <option value="">{'—'} Select a route {'—'}</option>
                {routes.map(r => (
                  <option key={r.routeId} value={r.routeId}>{r.routeName}</option>
                ))}
              </select>
              <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          </FieldGroup>

          <FieldGroup label="Boundary Color" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: '#f8fafc' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)', border: '2px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }} />
                <input type="color" value={newGeofence.color} onChange={e => setNewGeofence({ ...newGeofence, color: e.target.value })} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
              </div>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: newGeofence.color, border: '2px solid #e2e8f0', boxShadow: `0 0 0 3px ${newGeofence.color}28`, flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500, letterSpacing: '0.03em' }}>{newGeofence.color.toUpperCase()}</span>
              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>Click to pick</span>
            </div>
          </FieldGroup>

          <SectionHeader label="Draw Boundary" style={{ marginBottom: 14 }} icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 11 22 2 13 21 11 13 3 11" fill="rgba(255,255,255,0.3)" />
            </svg>
          } />

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Boundary Type</span>
              <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11, padding: '1px 7px', background: '#f1f5f9', borderRadius: 20 }}>Click to draw on map</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {SHAPE_TYPES.map(shape => {
                const isActive = activeShape === shape.id;
                const isDrawing = drawingMode && activeShape === shape.id;
                return (
                  <button
                    key={shape.id}
                    onClick={() => activateShape(shape.id)}
                    disabled={!mapsLoaded}
                    className="gf-shape-btn"
                    style={{
                      padding: '13px 8px 11px', borderRadius: 11,
                      border: isActive ? '2px solid #0f4a45' : '2px solid #e0f4f2',
                      background: isActive ? 'linear-gradient(145deg, #ccfbf1, #e8f4fb)' : '#f8fafc',
                      cursor: mapsLoaded ? 'pointer' : 'not-allowed',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                      transition: 'all 0.18s', position: 'relative', outline: 'none',
                      boxShadow: isActive ? '0 2px 10px rgba(26,122,115,0.18)' : 'none',
                    }}
                  >
                    {isDrawing && (
                      <span style={{ position: 'absolute', top: -5, right: -5, width: 12, height: 12, borderRadius: '50%', background: '#0f4a45', animation: 'pulse 1.2s infinite', boxShadow: '0 0 0 3px rgba(26,122,115,0.2)' }} />
                    )}
                    {shape.icon(isActive)}
                    <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#0f4a45' : '#64748b', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                      {shape.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {drawingMode && (
            <StatusBadge
              color="#4338ca" bg="#eef2ff" border="#c7d2fe"
              icon={<span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0f4a45', animation: 'pulse 1.2s infinite', display: 'inline-block', flexShrink: 0 }} />}
              text={
                activeShape === 'circle' && !circleCenterRef.current
                  ? 'Click on map to set circle center'
                  : activeShape === 'circle'
                  ? 'Move mouse to size · Click to finish'
                  : `Drawing ${activeShape} — click to add points · double-click to finish`
              }
            />
          )}

          {shapeBoundaryReady && !drawingMode && (
            <StatusBadge
              color="#15803d" bg="#f0fdf4" border="#bbf7d0"
              icon={<CheckCircle2 size={13} color="#16a34a" />}
              text={`Boundary ready — ${newGeofence.area.toLocaleString()} m²`}
            />
          )}

          {errors.boundary && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, color: '#ef4444', fontSize: 12 }}>
              <AlertCircle size={12} />{errors.boundary}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button
              onClick={resetForm}
              style={{
                flex: '0 0 auto', padding: '11px 16px', borderRadius: 10,
                background: '#fff', color: '#64748b', border: '1.5px solid #e2e8f0',
                fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                transition: 'border-color 0.2s, color 0.2s, background 0.2s',
                fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#fff'; }}
            >
              <RotateCcw size={14} /> Reset
            </button>

            <button
              onClick={createGeofenceHandler}
              disabled={saving}
              style={{
                flex: 1, padding: '11px', borderRadius: 10,
                background: saving ? 'linear-gradient(135deg, #6ea8c9 0%, #82b8d6 100%)' : 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)',
                color: '#fff', border: 'none', fontWeight: 700, fontSize: 13.5,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: saving ? 'none' : '0 4px 14px rgba(26,122,115,0.35)',
                transition: 'all 0.2s', letterSpacing: '0.01em', fontFamily: "'DM Sans', sans-serif",
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {saving ? (
                <>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                  Saving…
                </>
              ) : (
                <><Plus size={15} /> Create Geofence</>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* MAP PANEL */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>

        {!isFullscreen && (
          <div style={{
            padding: '5px 14px',
            background: 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)',
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                <line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/>
              </svg>
            </div>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, flex: 1 }}>Geofence Map</span>
            <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
            </span>
          </div>
        )}

        {/* Search bar */}
        {!isFullscreen && (
          <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'relative', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 9, padding: '6px 10px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search city, area, landmark or coordinates…"
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#0f172a', background: 'transparent', fontFamily: "'DM Sans', sans-serif" }}
              />
              {isSearching && (
                <div style={{ width: 14, height: 14, border: '2px solid #e2e8f0', borderTop: '2px solid #0f4a45', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
              )}
              {searchQuery && !isSearching && (
                <button onClick={clearSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 12, right: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0 0 10px 10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}>
                {searchResults.map((result) => (
                  <div
                    key={result.place_id}
                    onClick={() => selectSearchResult(result)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444" stroke="none" style={{ flexShrink: 0, marginTop: 2 }}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{result.structured_formatting?.main_text || result.description}</div>
                      {result.structured_formatting?.secondary_text && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{result.structured_formatting.secondary_text}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div ref={mapPanelRef} style={{ flex: 1, position: 'relative', zIndex: 1, minHeight: 0 }}>
          <div ref={mapDivRef} style={{ width: '100%', height: '100%' }} />

          {!mapsLoaded && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(145deg, #f0f4ff 0%, #f8faff 40%, #eef2ff 70%, #f5f7ff 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 14, zIndex: 10,
            }}>
              <span style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e0e7ff', borderTopColor: '#0f4a45', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>Loading Google Maps…</span>
            </div>
          )}

          {mapControls}

          {drawingMode && (
            <div style={{
              position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              zIndex: 1000, background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(8px)',
              color: '#fff', borderRadius: 30, padding: '10px 22px',
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10,
              boxShadow: '0 4px 20px rgba(0,0,0,0.28)', letterSpacing: '0.01em', pointerEvents: 'none',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2dd4bf', animation: 'pulse 1s infinite', flexShrink: 0 }} />
              {activeShape === 'polygon' && 'Click to place vertices · Double-click to finish polygon'}
              {activeShape === 'circle' && !circleCenterRef.current && 'Click to set center'}
              {activeShape === 'circle' && circleCenterRef.current && 'Move mouse to size · Click to finish circle'}
              {activeShape === 'line' && 'Click to place points · Double-click to finish line'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Small reusable UI helpers
───────────────────────────────────────────── */

const SectionHeader = ({ label, icon, style = {} }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '1.5px solid #e0f4f2', ...style }}>
    <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(26,122,115,0.30)' }}>
      {icon}
    </div>
    <span style={{ fontSize: 12, fontWeight: 800, color: '#0f3d3a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
  </div>
);

const FieldGroup = ({ label, required, optional, children, style = {} }) => (
  <div style={style}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
      {required && <span style={{ color: '#ef4444', fontSize: 12 }}>*</span>}
      {optional && <span style={{ fontWeight: 400, color: '#94a3b8', textTransform: 'none', fontSize: 11, letterSpacing: 0 }}>(optional)</span>}
    </label>
    {children}
  </div>
);

const FieldError = ({ msg }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, color: '#ef4444', fontSize: 12 }}>
    <AlertCircle size={11} /> {msg}
  </div>
);

const StatusBadge = ({ color, bg, border, icon, text }) => (
  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 9, background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
    {icon}
    <span style={{ fontSize: 12, color, fontWeight: 600 }}>{text}</span>
  </div>
);

const MapIconBtn = ({ onClick, children, title, active = false, danger = false }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: 38, height: 38, borderRadius: 10,
      background: active ? (danger ? '#fef2f2' : '#ccfbf1') : '#fff',
      border: `1.5px solid ${active ? (danger ? '#fecaca' : '#99f6e4') : '#e2e8f0'}`,
      boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s', padding: 0,
    }}
  >
    {children}
  </button>
);

/* ─────────────────────────────────────────────
   Main GeoFencingView with Tabs
───────────────────────────────────────────── */
const GeoFencingView = () => {
  const [activeTab, setActiveTab] = useState('create');

  const TABS = [
    {
      id: 'create',
      label: 'Create Geofence',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      ),
    },
    {
      id: 'assign',
      label: 'Assign Geofence',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      ),
    },
  ];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 60px)',
      fontFamily: "'DM Sans', sans-serif",
      background: 'linear-gradient(145deg, #f0f4ff 0%, #f8faff 40%, #eef2ff 70%, #f5f7ff 100%)',
    }}>
      <div style={{
        background: '#ffffff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 22px', flexShrink: 0,
        borderBottom: '1.5px solid #e0f4f2',
        boxShadow: '0 2px 12px rgba(26,122,115,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(26,122,115,0.30)', flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, letterSpacing: '-0.01em' }}>Geofencing</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, lineHeight: 1.3 }}>
              {activeTab === 'create'
                ? 'Draw boundaries and define custom geofence zones on the map'
                : 'Assign geofences to vehicle routes with alert schedules'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 11, padding: '4px', border: '1.5px solid #e2e8f0', gap: 3 }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '7px 22px', borderRadius: 8, border: 'none',
                  background: isActive ? 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)' : 'transparent',
                  color: isActive ? '#ffffff' : '#64748b',
                  fontWeight: isActive ? 700 : 500, fontSize: 13,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                  transition: 'all 0.18s ease', whiteSpace: 'nowrap',
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: isActive ? '0 2px 10px rgba(15,76,117,0.30)' : 'none',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#e0f4f2'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ display: 'flex', alignItems: 'center', opacity: isActive ? 1 : 0.6 }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        {activeTab === 'create' && <CreateGeofenceView />}
        {activeTab === 'assign' && (
          <div style={{ height: '100%', overflow: 'auto' }}>
            <AssignGeofencing userId={null} edit={false} editData={null} onEditModeChange={() => {}} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .gm-style-cc { display: none !important; }
        .gm-style .gm-style-iw-c { padding: 0 !important; border-radius: 12px !important; box-shadow: 0 6px 24px rgba(0,0,0,0.16) !important; }
        .gm-style .gm-style-iw-d { overflow: hidden !important; padding: 0 !important; }
        .gm-style .gm-style-iw-chr { position: absolute !important; top: 6px !important; right: 6px !important; height: 22px !important; }
        .gm-style .gm-style-iw-chr button { width: 22px !important; height: 22px !important; }
        .gm-style .gm-style-iw-chr button span { width: 14px !important; height: 14px !important; margin: 0 !important; }
      `}</style>
    </div>
  );
};

export default GeoFencingView;
